/**
 * Rename Action
 * Renames a file or folder using the shared moveItems logic
 */

import { Datum } from "../datastructure";
import { ActionDefinition, ActionContext } from "./types";
import { moveItems } from "./moveItems";
import { isReadOnlyDatum } from "../specialFolders";

/**
 * Rename a file or folder
 * Uses moveItems with newName to get optimistic updates and rollback
 */
export async function renameDatum(
  datum: Datum,
  newName: string,
  context: ActionContext
): Promise<void> {
  if (!newName || newName.trim() === "") {
    context.showToast?.("Name cannot be empty", "error");
    return;
  }

  if (newName === datum.name) {
    return; // No change
  }

  if (!context.filetree) {
    console.error("renameDatum: Filetree not available in context");
    context.showToast?.("Unable to rename - please try again", "error");
    return;
  }

  // Get the parent folder as the target (rename = move to same parent with new name)
  const parentDatum = datum.parent;
  if (!parentDatum) {
    console.error("renameDatum: Cannot rename root or orphaned datum");
    context.showToast?.("Unable to rename this item", "error");
    return;
  }

  await moveItems({
    items: [datum],
    target: parentDatum,
    filetree: context.filetree,
    context,
    newName,
  });
}

/**
 * Action definition for context menu
 * Note: This action requires user input (new name), so the execute function
 * will need to be called with the new name after prompting the user
 */
export const renameAction: ActionDefinition = {
  config: {
    action: "rename",
    label: "Rename",
  },
  // Renaming moves the datum within the live tree, so it is only offered where
  // there is a tree to move it in — `renameDatum` early-returns otherwise, and
  // a menu item that silently does nothing is worse than an absent one.
  isApplicable: (datum, context) =>
    Boolean(context?.filetree) &&
    datum.path.length > 0 &&
    !isReadOnlyDatum(datum, context),
  execute: async (datum, context) => {
    // This is a placeholder - actual implementation needs UI for input
    // The caller should prompt for the new name and call renameDatum directly
    const newName = window.prompt("Enter new name:", datum.name);
    if (newName) {
      await renameDatum(datum, newName, context);
    }
  },
};
